import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";

/**
 * The full-page loader (Loading.js `page`): the flat Fusion MSI star turning over
 * like an hourglass. It rests, flips a half turn, rests, and flips again, for as
 * long as the wait lasts.
 */

const BLUE_PATH = "M50 0 Q44 44 0 50 Q44 56 50 100 Z";
const GOLD_PATH = "M50 0 Q56 44 100 50 Q56 56 50 100 Z";

// One loop is two flips: rest 1s, turn 0.6s, rest 1s, turn 0.6s.
const REST_MS = 1000;
const TURN_MS = 600;
const LOOP_MS = 2 * (REST_MS + TURN_MS);
const pct = (ms) => `${((ms / LOOP_MS) * 100).toFixed(2)}%`;

// ⚠️ A HALF TURN SWAPS THE COLOURS (blue ends up on the right), so the loop runs to
// 360°, not back to 0°: two flips per loop, or every loop would end in a snap.
const flip = keyframes`
  0%, ${pct(REST_MS)} { transform: rotate(0deg); }
  ${pct(REST_MS + TURN_MS)}, ${pct(2 * REST_MS + TURN_MS)} { transform: rotate(180deg); }
  100% { transform: rotate(360deg); }
`;

export default function FusionLoader({ size = 72 }) {
  return (
    <Box
      // An indeterminate progress bar to assistive tech, like the CircularProgress it replaces.
      role="progressbar"
      aria-label="Loading"
      sx={{ width: size, height: size }}
    >
      <Box
        component="svg"
        viewBox="0 0 100 100"
        aria-hidden
        sx={{
          width: "100%",
          height: "100%",
          overflow: "visible",
          animation: `${flip} ${LOOP_MS}ms cubic-bezier(0.65, 0, 0.35, 1) infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <Box
          component="path"
          d={BLUE_PATH}
          sx={({ palette }) => ({ fill: palette.brand?.blue || palette.secondary.main })}
        />
        <Box
          component="path"
          d={GOLD_PATH}
          sx={({ palette }) => ({ fill: palette.brand?.gold || palette.warning.main })}
        />
      </Box>
    </Box>
  );
}
